// HU-09 y HU-46 · El expediente del caso. F-02, Sprint 1.
//
// Lo que el usuario ve al terminar de enviar: que el caso ya tiene numero, que su
// expediente existe y que documentos lleva dentro. Es la confirmacion de que la
// frontera de RN-01 se cruzo — antes de enviar no habia nada de esto.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Archive, CheckCircle2, Clock, Download, Eye, FileCheck, FileWarning, FolderOpen, Inbox,
  Loader2, Paperclip, RotateCw, ShieldAlert, X,
} from 'lucide-react';
import {
  Alert, Button, Card, Codigo, DataTable, EmptyState, L2Tag, Mono, PageHeader, StatusBadge,
} from '../components/ui';
import { contenidoDeDocumento, contenidoDeEvidencia, reintentarEncargo, verExpediente } from '../api/expedientes';
import { ACCION_REGISTRAR_DISPOSICION, ACCION_VER_AVISOS, ACCION_VER_TRAZABILIDAD } from '../api/expedientes';
import type {
  CasoDelExpediente, DisposicionDelExpediente, FilaDelExpediente, VersionDeLaFila,
} from '../api/expedientes';
// HU-27 · HU-28: la cabecera del caso y el historial de cierres, de `caso.*` (ui/MG-ILE-54).
import { CabeceraDelCaso, HistorialDeCierres } from '../components/CasoDelExpediente';
// HU-26 · HU-48 · HU-52: la tabla de documentos por gravedad, con versiones y «Reintentar».
import DocumentosDelExpediente, { claveDeLaFila, tipoEnKebab } from '../components/DocumentosDelExpediente';
// HU-25: las disposiciones de la autoridad (`expediente.disposiciones[]`) y «Registrar
// disposición» con el caso fijado, si `caso.acciones_permitidas` lo trae.
import DisposicionesDelCaso from '../components/DisposicionesDelCaso';
import { etiquetaDelExpediente, TONOS_DEL_EXPEDIENTE } from '../lib/etiquetas';
// BUG-O11 · el acuse y la carta se pintan con el texto que manda el servidor, con la MISMA
// insignia que el trámite y la carta (no se traduce `estado_cargo` aquí).
import EstadoDelAcuse from '../components/EstadoDelAcuse';
import { tonoDeLaCarta } from '../lib/etiquetas';
// HU-27 · el cierre del caso. Tarjeta autonoma: se pide ella misma los controles y
// decide si le toca pintarse (bug 702, mas abajo).
import CierreDelCaso from '../components/CierreDelCaso';
// HU-47 · la trazabilidad de a quien se aviso vive en el contexto de notificaciones.
import { consultarAvisosDelCaso } from '../api/destinatarios';
import { esEntregaFallida, rotuloDeEntrega } from '../lib/avisos';
import { descargarBlob } from '../lib/entradas';
import { nombreDelTipoDeAviso } from '../lib/tiposDeAviso';
import { comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import type { ReactNode, RefObject } from 'react';
import type { ColumnaDeTabla, Icono } from '../components/ui';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { RespuestaDelApi } from '../tipos';

const NOMBRE_DE_TIPO: Record<string, string> = {
  formato_verde: 'Formato Verde',
  formato_amarillo: 'Formato Amarillo',
  informe: 'Informe FAR-03',
  carta: 'Carta a la autoridad',
  constancia: 'Constancia',
  cargo: 'Cargo de presentación',
};

// El expediente NACE ASINCRONO: enviar el caso deja un encargo en la bandeja de
// salida y el despachador lo materializa segundos (a veces un par de minutos)
// despues. Un solo GET justo tras enviar encuentra 404 — que NO es un fallo, es
// "todavia": la pantalla espera y reintenta sola antes de rendirse. Salio a la
// luz con el PRIMER envio real desde la pantalla (PE-018, 28-ago-2026).
const REINTENTO_MS = 5000;
const REINTENTOS_MAXIMOS = 36; // ~3 minutos: el ciclo del despachador + margen

// T-42 · La espera post-envio, contada por etapas. Del envio nacen tres cosas y
// no a la vez: el numero (en la MISMA transaccion), el expediente (lo abre el
// despacho segundos despues) y el Formato Verde (lo archiva el worker). La
// pantalla anterior fundia las tres en un solo "Generando el expediente…" opaco
// que el operador vivio como eterno (28-ago-2026): aqui cada una es una etapa
// que se marca sola, y la espera no secuestra — la bandeja queda a un clic y el
// sondeo muere limpio con el desmontaje.
const PINTA_DE_ETAPA: Record<string, { icono: Icono; clase: string }> = {
  hecha: { icono: CheckCircle2, clase: 'text-emerald-600' },
  pendiente: { icono: Loader2, clase: 'text-blue-500 animate-spin' },
  // Agotar el sondeo NO es fallar: el documento sigue en camino (el worker puede
  // ir con retraso) y refrescar mas tarde lo encontrara.
  en_camino: { icono: Clock, clase: 'text-amber-500' },
};

function Etapa({ estado, titulo, detalle }: { estado: string; titulo: ReactNode; detalle?: ReactNode }) {
  const { icono: Icono, clase } = PINTA_DE_ETAPA[estado];
  return (
    // `data-estado` es el contrato legible del estado: el icono es decorativo y
    // el texto del detalle cambia con la etapa.
    <li className="flex items-start gap-2.5" data-estado={estado}>
      <Icono size={16} className={'shrink-0 mt-0.5 ' + clase} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-body font-semibold text-ink">{titulo}</p>
        {detalle && <p className="text-meta text-ink-soft mt-0.5">{detalle}</p>}
      </div>
    </li>
  );
}

function EtapasDelNacimiento({ numero, expedienteAbierto, formatoArchivado, formatoEnCamino, onIrABandeja, tropiezo }: {
  numero?: string | null;
  expedienteAbierto: boolean;
  formatoArchivado: boolean;
  formatoEnCamino: boolean;
  onIrABandeja?: () => void;
  /** El error que impidio nacer al expediente, si lo hubo. */
  tropiezo?: Fallo | null;
}) {
  let estadoDelFormato = 'pendiente';
  if (formatoArchivado) estadoDelFormato = 'hecha';
  else if (formatoEnCamino) estadoDelFormato = 'en_camino';
  const todoListo = expedienteAbierto && formatoArchivado;
  return (
    <Card
      className="mb-4"
      title={numero ? `Caso ${numero} registrado correctamente` : 'Caso registrado correctamente'}
      subtitle="El expediente y su Formato Verde estarán listos en unos segundos."
    >
      <ul className="space-y-3">
        <Etapa
          estado="hecha"
          titulo="Caso registrado"
          detalle={numero
            ? <>Número <Mono>{numero}</Mono>, asignado al enviar.</>
            : 'El número se asignó al enviar el caso.'}
        />
        <Etapa
          estado={expedienteAbierto ? 'hecha' : 'pendiente'}
          titulo="Expediente abierto"
          detalle={expedienteAbierto
            ? 'Se avisó al Comité, a Gerencia General y a los VP.'
            : 'Se está abriendo; aparecerá aquí en unos segundos.'}
        />
        <Etapa
          estado={estadoDelFormato}
          titulo="Formato Verde archivado"
          detalle={{
            hecha: 'Ya está en la lista de documentos del expediente.',
            pendiente: 'Se está generando y archivando; suele tardar menos de un minuto.',
            en_camino: 'Sigue en camino: refresca en unos minutos. El caso ya quedó registrado.',
          }[estadoDelFormato]}
        />
      </ul>
      {/* CP-08-13 · EL TROPIEZO VA AQUI DENTRO, NO EN LUGAR DE ESTO.
          HU-08: "el caso NO se pierde: queda marcado para reintento Y yo recibo
          confirmacion de que el registro se guardo". Las dos mitades, no una. Un
          problema al ABRIR el expediente no desmiente que el caso este registrado
          y numerado —el numero nacio en la transaccion del envio, que ya confirmo—,
          asi que se cuenta como lo que es: un tropiezo de la parte asincrona. */}
      {tropiezo && (
        <div className="mt-4">
          <Alert tone="warning" title="El expediente todavía no se pudo abrir">
            Tu caso <b>quedó registrado y numerado</b>. El expediente se volverá a generar
            solo; puedes seguir trabajando.
            <p className="mt-1.5 text-meta opacity-80">Detalle técnico: {tropiezo.detalleTecnico ?? tropiezo.message}</p>
          </Alert>
        </div>
      )}
      {/* La salida de la espera: nadie queda preso mirando el reloj. Si se va,
          el proceso sigue en el servidor y esta pantalla estara completa cuando
          vuelva (el sondeo local se cancela con el desmontaje, no el trabajo). */}
      {!todoListo && onIrABandeja && (
        <div className="mt-4 pt-3.5 border-t border-hairline flex flex-wrap items-center justify-between gap-3">
          <p className="text-meta text-ink-soft">
            Puedes volver a la bandeja: el proceso sigue solo.
          </p>
          <Button size="sm" variant="secondary" icon={Inbox} onClick={onIrABandeja}>
            Ir a la bandeja
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * HU-47 · A QUIEN SE AVISO (CP-47-06 y CP-47-10, 5-sep-2026).
 *
 * "Cuando abro su trazabilidad, veo la lista de personas y correos a los que se aviso, y
 * esa lista es la que estaba vigente en el momento del envio." No habia donde abrirla: el
 * endpoint existia y el frontend no lo llamaba desde ningun sitio.
 *
 * Va en el expediente porque es donde ya se mira lo que paso con el caso despues de
 * enviarlo. El vacio V-12 dejaba la ubicacion abierta; si el PO la quiere en otro lado,
 * este bloque se mueve entero.
 *
 * AISLADO A PROPOSITO: si esta consulta falla, se dice AQUI DENTRO y el resto del
 * expediente sigue funcionando. Es la leccion de CP-08-13 —una llamada secundaria que
 * falla no puede llevarse por delante la pantalla entera—.
 */
type Notificacion = {
  fecha_recepcion: string; autoridad: string | null; estado_carta: string | null; firmante: string | null;
  presentado_por: string | null; fecha_presentacion: string | null; estado_cargo: string | null; fecha_cargo: string | null;
  /** BUG-O11 (AT S3 §2.2.4): «Acuse pendiente» · «Acuse demorado» · «Cargo recibido · dd/mm/aaaa»; null sin carta enviada. */
  texto_del_acuse: string | null;
  /** El mismo hecho como booleano: solo decide el tono de la insignia. */
  acuse_demorado: boolean;
  /** «Enviada · v1 · plantilla SENASA v3»: lo arma el servidor, igual que en el trámite. */
  texto_de_la_carta: string | null;
  /**
   * CC-02 (B9, RN-80): las cartas activas por `orden`, cada una con su presentación y su acuse.
   * La raíz es la carta principal. Opcional: un servidor anterior no la trae.
   */
  cartas?: CartaNotificada[];
};

/** CC-02 (B9): una carta de la notificación (`CartaDeLaNotificacionSalida`). */
type CartaNotificada = {
  autoridad_codigo: string; autoridad: string; estado_carta: string | null; firmante: string | null;
  presentado_por: string | null; fecha_presentacion: string | null; estado_cargo: string | null; fecha_cargo: string | null;
  texto_del_acuse: string | null; acuse_demorado: boolean; texto_de_la_carta: string | null;
};

/**
 * Lo de UNA carta en «Notificación a la autoridad»: presentación, carta, firmante, quién presentó
 * y acuse, con los textos del servidor (BUG-O11). Con una sola carta es la raíz, como siempre.
 */
function DatosDeLaCarta({ c, zona }: { c: Omit<CartaNotificada, 'autoridad_codigo' | 'autoridad'>; zona: string | null }) {
  return (
    <>
      <dt className="text-ink-soft">Presentación a la autoridad</dt><dd><Mono>{comoFechaDeInstante(c.fecha_presentacion, zona) || '—'}</Mono></dd>
      <dt className="text-ink-soft">Carta</dt>
      <dd data-testid="expediente-carta">
        {c.texto_de_la_carta ? <StatusBadge tone={tonoDeLaCarta(c.estado_carta)}>{c.texto_de_la_carta}</StatusBadge> : '—'}
      </dd>
      <dt className="text-ink-soft">Firmante</dt><dd>{c.firmante ?? '—'}</dd>
      <dt className="text-ink-soft">Presentó</dt><dd>{c.presentado_por ?? '—'}</dd>
      {/* Sin carta enviada (o sin trámite: caso no grave) no hay acuse que esperar. */}
      {c.texto_del_acuse && (
        <>
          <dt className="text-ink-soft">Acuse de la autoridad</dt>
          <dd data-testid="expediente-cargo"><EstadoDelAcuse acuse={c} /></dd>
        </>
      )}
    </>
  );
}

/**
 * CC-02 (B9, RN-80; `ui/MG-ILE-1076.md` «Notificación a la autoridad»): con VARIAS cartas, la
 * recepción por ILENDER es una (es del caso) y cada carta dice lo suyo bajo el nombre de su
 * autoridad. El subtítulo lista los destinos como la bandeja general («SENASA · SANIPES»).
 */
function NotificacionPorCarta({ n, cartas, zona }: { n: Notificacion; cartas: CartaNotificada[]; zona: string | null }) {
  return (
    <Card className="mt-4" title="Notificación a la autoridad" icon={FileCheck}
      subtitle={`Trámite ante ${cartas.map((c) => c.autoridad).join(' · ')}.`}>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-meta" aria-label="Notificación a la autoridad">
        <dt className="text-ink-soft">Recepción por ILENDER</dt><dd><Mono>{comoFechaDeInstante(n.fecha_recepcion, zona)}</Mono></dd>
      </dl>
      {cartas.map((c) => (
        <section key={c.autoridad_codigo} className="mt-3 pt-3 border-t border-hairline" aria-label={`Carta a ${c.autoridad}`}
          data-testid="expediente-notificacion-carta" data-autoridad={c.autoridad_codigo}>
          <h3 className="text-body font-semibold text-ink">Carta a {c.autoridad}</h3>
          <dl className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-meta">
            <DatosDeLaCarta c={c} zona={zona} />
          </dl>
        </section>
      ))}
    </Card>
  );
}


/**
 * HU-21-C9: «Recepción por ILENDER: 2026-09-03» y «Presentación a la autoridad: 2026-09-15».
 *
 * BUG-O11 (O-11, 29-sep-2026): la fila del cargo traducía `estado_cargo` a mano y decía
 * «Acuse pendiente» cuando la bandeja, con los días hábiles, ya decía «Acuse demorado». Ahora
 * la carta y el acuse se pintan con `texto_de_la_carta` y `texto_del_acuse` tal cual
 * (`ui/MG-ILE-54.md`, testids `expediente-carta` y `expediente-cargo`). Sin texto del acuse
 * (la carta no salió o no hay trámite) no hay acuse que mostrar y la fila no se pinta.
 */
function NotificacionDelCaso({ n, zona }: { n: Notificacion; zona: string | null }) {
  const cartas = n.cartas ?? [];
  if (cartas.length > 1) return <NotificacionPorCarta n={n} cartas={cartas} zona={zona} />;
  return (
    <Card className="mt-4" title="Notificación a la autoridad" icon={FileCheck}
      subtitle={n.autoridad ? `Trámite ante ${n.autoridad}.` : 'Sin trámite regulatorio (el caso no es grave o aún no se decidió).'}>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-meta" aria-label="Notificación a la autoridad">
        <dt className="text-ink-soft">Recepción por ILENDER</dt><dd><Mono>{comoFechaDeInstante(n.fecha_recepcion, zona)}</Mono></dd>
        <DatosDeLaCarta c={n} zona={zona} />
      </dl>
    </Card>
  );
}

type Evidencia = {
  adjunto_id: string; nombre_archivo: string; origen: string; etiqueta_de_origen: string; huella_sha256: string;
  tipo_mime: string; tamano_bytes: number; contiene_dato_de_personas: boolean; subido_en: string;
  subido_por: string | null; subido_por_nombre: string | null;
  /** HU-26-C5 (AT S3 §2.2.4): «Sustento del Comité · subido por M. Díaz»; vacío con un servidor anterior. */
  texto_del_origen?: string;
};

const tamano = (bytes: number) => (bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);

/**
 * HU-49-C9: «cada archivo muestra su origen … y el usuario que lo subió» — Y SE ABRE.
 *
 * Hasta el 22-sep-2026 esta tarjeta solo nombraba los archivos. No es que el botón
 * estuviera escondido: no existía endpoint que los sirviera, porque el binario sube
 * directo al almacenamiento con una URL firmada de ESCRITURA (HU-07, ADR-015) y nadie
 * emitía nunca la de lectura. Un archivo que se nombra y no se abre no es sustento de
 * nada, y el sustento es lo que el expediente existe para guardar.
 *
 * La marca L2 se queda como estaba: es un aviso de lo que se va a abrir, no un
 * bloqueo. Quien no tenga el permiso recibe 403 y el mensaje se lo explica.
 */
/**
 * Qué se le dice al usuario cuando un archivo no se puede abrir (22-sep-2026).
 *
 * TRES DESENLACES, NO DOS. Hasta hoy «el archivo no está en el almacén» y «los bytes no
 * producen la huella declarada» compartían el código `custodia_rota` y el MISMO texto, el
 * de la huella. El operador se topó con ello con un `.env` que mezclaba dos ambientes de
 * Azure —base de uno, blobs de otro—: la pantalla le decía que el archivo podía estar
 * manipulado, que es la alarma más cara del sistema, por una variable mal apuntada.
 *
 * NO se pinta el `detalle` que manda el servidor aunque sea lo más rápido: trae la ruta
 * del blob y el nombre del contenedor, y eso es plomería, no algo que se le enseñe a
 * quien usa el sistema. Va al log y a la respuesta del API, no a la pantalla.
 */
function porQueNoSeAbre(fallo: Fallo, nombre?: string): { tono: 'warning' | 'danger'; titulo: string; texto: string } {
  const suyo = nombre ? `«${nombre}»` : 'El documento';
  if (fallo.estado === 403) {
    return {
      tono: 'warning',
      titulo: 'No tienes permiso para abrir este archivo',
      texto: `${suyo} contiene datos de una persona (L2). Pide el permiso al administrador. El intento quedó registrado en la auditoría.`,
    };
  }
  if (fallo.cuerpo?.codigo === 'binario_ausente') {
    // Ni el usuario ni el administrador del negocio pueden hacer nada, pero tampoco hay
    // indicio de manipulación: el registro está y el archivo no aparece en ese almacén.
    // Se dice qué pasó y a quién le toca, sin acusar a nadie de romper la custodia.
    return {
      tono: 'warning',
      titulo: 'El archivo no está en el almacén documental',
      texto: `${suyo} figura en el expediente, pero no se encuentra su archivo. Avisa a soporte.`,
    };
  }
  if (fallo.cuerpo?.codigo === 'custodia_rota') {
    return {
      tono: 'danger',
      titulo: 'El documento no supera la verificación de custodia',
      texto: `El contenido de ${suyo.toLowerCase()} no coincide con el documento que se emitió. No lo uses y avisa a soporte.`,
    };
  }
  return { tono: 'danger', titulo: 'No se pudo abrir el documento', texto: fallo.message };
}

function EvidenciasDelCaso({ casoId, evidencias, zona }: { casoId: string; evidencias: Evidencia[]; zona: string | null }) {
  // El fallo es POR TARJETA y no por fila: un aviso por fila multiplicaría el mismo
  // texto y descuadraría la lista, que es lo que el operador señaló en los botones de
  // tabla. Se guarda cuál falló para nombrarlo en el aviso.
  const [fallo, setFallo] = useState<{ nombre: string; fallo: Fallo } | null>(null);
  const [bajando, setBajando] = useState<string | null>(null);

  const descargar = useCallback(async (evidencia: Evidencia) => {
    setFallo(null);
    setBajando(evidencia.adjunto_id);
    try {
      descargarBlob(await contenidoDeEvidencia(casoId, evidencia.adjunto_id), evidencia.nombre_archivo);
    } catch (error) {
      setFallo({ nombre: evidencia.nombre_archivo, fallo: aFallo(error) });
    } finally {
      setBajando(null);
    }
  }, [casoId]);

  return (
    <Card className="mt-4" title="Evidencias y sustento" icon={Paperclip} data-testid="expediente-sustento"
      subtitle="Lo que adjuntaron el registrador, el Comité y la autoridad, con su origen y quién lo subió.">
      {fallo && (
        /* `Alert` no acepta `className` —es del catalogo y sus variantes son fijas—,
           asi que la separacion la pone el contenedor. */
        <div className="mb-3">
        <Alert
          tone={porQueNoSeAbre(fallo.fallo, fallo.nombre).tono}
          title={porQueNoSeAbre(fallo.fallo, fallo.nombre).titulo}
          onCerrar={() => setFallo(null)}
        >
          {porQueNoSeAbre(fallo.fallo, fallo.nombre).texto}
        </Alert>
        </div>
      )}
      {evidencias.length === 0
        ? <p className="text-meta text-ink-soft">El caso no tiene archivos adjuntos.</p>
        : (
          <ul className="divide-y divide-hairline" aria-label="Evidencias del caso">
            {evidencias.map((e) => (
              <li key={e.adjunto_id} data-testid="expediente-sustento-fila" className="py-2 flex flex-wrap items-center gap-2 text-meta">
                <span className="font-medium">{e.nombre_archivo}</span>
                {/* HU-26-C1 y C5: origen y autor en UN texto del servidor; el sustento del
                    Comité no se mezcla con la evidencia del registrador (RN-34). */}
                {e.texto_del_origen ? (
                  <span className="text-ink-soft" data-testid="expediente-sustento-fila-origen">{e.texto_del_origen}</span>
                ) : (
                  // Un servidor anterior al Sprint 3 no trae el texto: origen y autor por separado, como antes.
                  <>
                    <StatusBadge tone={e.origen === 'comite' ? 'info' : 'neutral'}>{e.etiqueta_de_origen}</StatusBadge>
                    <span className="text-ink-soft">
                      subido por <span className="text-ink">{e.subido_por_nombre ?? (e.subido_por ? `usuario ${e.subido_por.slice(0, 8)}` : '—')}</span>
                    </span>
                  </>
                )}
                {e.contiene_dato_de_personas && <L2Tag>Datos de personas · L2</L2Tag>}
                <span className="text-ink-soft">{tamano(e.tamano_bytes)} · {comoFechaYHora(e.subido_en, zona)}</span>
                {/* `secondary` y no `ghost`: en una lista un botón sin borde no parece
                    un botón y solo se descubre al pasar el cursor. `ml-auto` lo alinea
                    a la derecha en todas las filas, tengan los badges que tengan. */}
                <Button size="sm" variant="secondary" icon={Download} className="ml-auto"
                  disabled={bajando === e.adjunto_id}
                  aria-label={`Descargar ${e.nombre_archivo}`}
                  onClick={() => { descargar(e); }}>
                  {bajando === e.adjunto_id ? 'Descargando…' : 'Descargar'}
                </Button>
              </li>
            ))}
          </ul>
        )}
    </Card>
  );
}

/**
 * Bug 1081 (V8): con VARIAS cartas el servidor lista un aviso por (tipo, autoridad), y el mismo
 * tipo sale una vez por carta. Cada uno dice entonces de qué autoridad es, con el nombre de su
 * carta. Con una sola carta nada cambia: el aviso no nombra la autoridad.
 */
const nombresDeLasCartas = (cartas: CartaNotificada[]): Map<string, string> | null =>
  cartas.length > 1 ? new Map(cartas.map((c) => [c.autoridad_codigo, c.autoridad])) : null;

const autoridadDelAviso = (aviso: RespuestaDelApi, nombres: Map<string, string> | null): string | null => {
  const codigo = aviso.autoridad_codigo as string | null | undefined;
  if (!nombres || !codigo) return null;
  return nombres.get(codigo) ?? codigo;
};

function AvisosDelCaso({ casoId, cartas = [] }: { casoId: string; cartas?: CartaNotificada[] }) {
  const [avisos, setAvisos] = useState<RespuestaDelApi[] | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);

  useEffect(() => {
    let vivo = true;
    // Envuelto en `async` y no encadenado con `.catch`: `consultarAvisosDelCaso` valida
    // el id ANTES de pedir nada y lanza de forma SINCRONA si no es un uuid. Un `.catch`
    // no atrapa eso, y la excepcion subiria hasta romper el expediente entero — justo lo
    // que esta seccion promete no hacer.
    (async () => {
      try {
        const r = await consultarAvisosDelCaso(casoId);
        if (vivo) setAvisos(r);
      } catch (e) {
        if (vivo) setFallo(aFallo(e));
      }
    })();
    return () => { vivo = false; };
  }, [casoId]);

  if (fallo) {
    return (
      <Card className="mt-4" title="Avisos enviados">
        <Alert tone="warning" title="No se pudo consultar a quién se avisó">
          El expediente se muestra igual; esto solo afecta a esta sección. {fallo.message}
        </Alert>
      </Card>
    );
  }
  if (!avisos) return null;

  // Un caso en borrador no dispara avisos (RN-01): lista vacia no es un fallo.
  if (!avisos.length) {
    return (
      <Card className="mt-4" title="Avisos enviados">
        <p className="text-meta text-ink-soft">
          Este caso todavía no tiene avisos. Los avisos salen al enviar el caso.
        </p>
      </Card>
    );
  }

  const nombres = nombresDeLasCartas(cartas);
  const conAutoridad = avisos.map((aviso) => ({ aviso, autoridad: autoridadDelAviso(aviso, nombres) }));
  return (
    <Card className="mt-4" title="Avisos enviados" data-testid="expediente-avisos"
      subtitle="A quién se avisó cuando se envió el caso.">
      {conAutoridad.map(({ aviso, autoridad }) => (
        <div key={`${aviso.tipo_aviso}·${aviso.autoridad_codigo ?? ''}`} className="mb-4 last:mb-0"
          data-autoridad={autoridad ? aviso.autoridad_codigo : undefined}>
          {/* La etiqueta del catálogo único (`lib/tiposDeAviso.ts`), la misma que la pantalla
              de destinatarios. Sin el código interno al lado (decisión del operador, 5-oct-2026). */}
          <p className="text-meta font-semibold text-ink mb-1.5">
            {nombreDelTipoDeAviso(aviso.tipo_aviso)}
            {autoridad && ` · ${autoridad}`}
          </p>

          {/* CP-47-10 · "el caso avanza igual, PERO el sistema registra que no habia a
              quien avisar, porque fallar en silencio seria peor". Se dice con todas las
              letras, no se deja una tabla vacia que se lea como un error de carga. */}
          {!aviso.hubo_destinatarios ? (
            /* EL MENSAJE DICE QUE FALTA Y DONDE SE ARREGLA (operador, 9-sep-2026).
               Antes decia "la lista de destinatarios estaba vacia", que describe el
               sintoma y deja al lector adivinando la causa: la lista NO esta vacia —hay
               destinatarios dados de alta— sino que ninguno cubre el pais de ESTE caso.
               Sin esa precision, el administrador mira la pantalla de destinatarios, ve
               cuatro personas configuradas y concluye que el sistema se equivoca.

               ⚠ Y LA ULTIMA FRASE NO ES UN ADORNO: sin ella, quien configure el pais
               ahora volvera aqui esperando ver la lista, y seguira viendo esto. La
               trazabilidad se congela al enviar (C-03), asi que configurar despues sirve
               para los casos NUEVOS, nunca para los ya enviados. Decirlo aqui evita un
               reporte de defecto que no lo es.

               No se nombra el pais porque no esta en esta respuesta: `aviso_enviado` no
               guarda esa columna y el expediente tampoco la trae. Nombrarlo obligaria a
               anadir el dato al contrato, y se elevo al operador en vez de inventarlo. */
            <Alert tone="warning" title="No había destinatarios configurados para este aviso">
              Al enviarse el caso, <b>ningún destinatario activo cubría su país</b> para este
              tipo de aviso, así que el correo no salió hacia nadie.
            </Alert>
          ) : (
            <>
              <p className="text-meta text-ink-soft mb-1.5">
                {aviso.enviados} enviados · {aviso.fallidos} fallidos · {aviso.pendientes} pendientes
              </p>
              <ul className="space-y-1.5">
                {aviso.entregas.map((e: RespuestaDelApi) => (
                  <li key={e.entrega_id} data-testid="expediente-avisos-fila"
                    className="flex items-start justify-between gap-2 px-3 py-2 bg-canvas border border-hairline rounded-xl">
                    <span className="text-meta text-ink min-w-0">
                      <b>{e.nombre ?? 'Sin nombre'}</b>
                      {e.cargo && <span className="text-ink-soft"> · {e.cargo}</span>}
                      <br />
                      <Mono>{e.correo}</Mono>
                    </span>
                    <span className="text-meta text-ink-soft text-right shrink-0">
                      {/* "no enviado", no "fallido" (7-sep-2026). Quien mira el expediente
                          no pregunta si la operacion fallo: pregunta si esa persona se
                          entero. Y no se entero.

                          Y SOLO EL ROTULO: nada de motivo (decision del operador,
                          7-sep-2026). Aqui se pinto primero `ultimo_error` crudo —la
                          excepcion del SDK de Azure, que llego a solaparse encima del
                          nombre del destinatario— y despues su traduccion; las dos
                          sobran. Esta lista responde A QUIEN SE AVISO, y para eso basta
                          con si llego o no.

                          ⚠ ACTUALIZADO EL 9-sep-2026: aqui decia que el PORQUE seguia
                          estando en el aviso de correos fallidos de la bandeja. Ese aviso
                          se retiro (decision del operador), asi que HOY EL MOTIVO NO SE
                          MUESTRA EN NINGUNA PANTALLA — sigue guardado en `ultimo_error` y
                          lo sirve `GET /avisos`, pero nadie lo pinta. Se deja escrito
                          para que la decision sea visible y no un olvido. */}
                      <span className={esEntregaFallida(e.estado) ? 'text-rose-700 font-medium' : undefined}>
                        {rotuloDeEntrega(e.estado)}
                      </span>
                      {e.enviado_en && <><br />{String(e.enviado_en).slice(0, 16).replace('T', ' ')}</>}
                      {/* LOS REINTENTOS (CP-10-01 y CP-10-04, 5-sep-2026). Los dos casos los
                          nombran con estas palabras: "fecha, resultado del envio y
                          reintentos". El servidor los devolvia desde el principio y esta
                          linea los tiraba.
                          Se dice "1 intento" y no "0 reintentos" porque es lo que se cuenta
                          de verdad: `intentos` incluye el primer envio. Con uno solo no se
                          escribe nada, para no llenar la lista de ruido cuando todo fue
                          bien; a partir del segundo si, porque entonces significa algo. */}
                      {e.intentos > 1 && <><br />{e.intentos} intentos</>}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}

/** Las acciones de la cabecera: volver, ver la trazabilidad, el archivado y la completitud. */
function AccionesDeLaCabecera({ expediente, volverA, onVolver, puedeVerTrazabilidad, onVerTrazabilidad }: {
  expediente: RespuestaDelApi;
  volverA: string | null;
  onVolver: (() => void) | undefined;
  /** El servidor ofrece `ver_trazabilidad` en `expediente.acciones_permitidas` (HU-37-C8). */
  puedeVerTrazabilidad: boolean;
  onVerTrazabilidad: (() => void) | undefined;
}) {
  return (
    // AL EXPEDIENTE SE ENTRA DESDE CINCO PANTALLAS (25-sep-2026). Sin salida, la
    // unica via era el menu lateral, que lleva al inicio de otro modulo y pierde
    // el filtro y la fila en la que estaba el usuario. El boton dice A DONDE
    // vuelve, no solo «Volver»: desde la bandeja del Comite y desde la de
    // Regulatorio la pantalla es la misma, y el nombre es lo que las distingue.
    <span className="flex flex-wrap items-center gap-2">
      {onVolver && volverA && (
        <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>
          Volver a {volverA}
        </Button>
      )}
      {onVerTrazabilidad && puedeVerTrazabilidad && (
        <Button variant="secondary" size="sm" icon={Clock} onClick={onVerTrazabilidad} data-testid="expediente-ver-trazabilidad">
          Ver trazabilidad
        </Button>
      )}
      {/* HU-27-C7 / HU-28-C6: «Archivado» al cerrar; vuelve a abierto al reabrir. */}
      {expediente.estado && (
        <StatusBadge tone={TONOS_DEL_EXPEDIENTE[expediente.estado] ?? 'neutral'} icon={Archive} data-testid="expediente-archivado">
          {etiquetaDelExpediente(expediente.estado)}
        </StatusBadge>
      )}
      {/* HU-26-C2 «Faltan 4» / HU-52-C4 «Expediente completo»: el texto es del servidor. */}
      <StatusBadge tone={expediente.esta_completo ? 'success' : 'warning'} icon={expediente.esta_completo ? FileCheck : FileWarning}
        data-testid="expediente-completitud">
        {expediente.texto_de_completitud || (expediente.esta_completo ? 'Completo' : `Faltan ${expediente.total_de_faltantes}`)}
      </StatusBadge>
    </span>
  );
}

/** HU-26-C2 · HU-52-C6: el aviso de lo que todavía falta, con el texto de cada faltante del servidor. */
function FaltantesDelExpediente({ expediente }: { expediente: RespuestaDelApi }) {
  return (
    <Alert tone="info" title="El expediente todavía no está completo" data-testid="expediente-incompleto">
      {/* HU-26-C2 · HU-52-C6 (fin de O-10): cada faltante con el texto del servidor
          («pendiente · se archiva al aprobarse el informe», «hay que aportarlo»). Ningún
          texto dice cuándo «llega» una copia: lo dice el estado del documento. El cargo
          lleva además de dónde sale («Lo devuelve la autoridad; alguien lo sube al
          recibirlo», HU-26-C4): no lo genera el sistema. */}
      {expediente.texto_de_completitud && <p className="font-semibold" data-testid="expediente-faltan">{expediente.texto_de_completitud}</p>}
      <ul className="mt-1 space-y-1">
        {(expediente.faltantes ?? []).map((f: RespuestaDelApi) => (
          // CC-02 (B9): con varias cartas hay un faltante del mismo tipo por autoridad.
          <li key={`${f.codigo}·${f.autoridad_codigo ?? ''}`} data-testid={`expediente-faltante-${tipoEnKebab(String(f.codigo))}`}>
            · {f.etiqueta}
            {f.texto_del_estado && <span className="text-ink-soft"> · {f.texto_del_estado}</span>}
            {f.momento_de_aparicion && (!f.lo_produce_el_sistema || !f.texto_del_estado) && (
              <span className="text-ink-soft"> ({f.momento_de_aparicion})</span>
            )}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/** T-38: el documento abierto en el visor y la URL de objeto que hay que revocar al cerrarlo. */
type DocumentoEnElVisor = { documentoId: string; nombre: string; url: string };

/**
 * T-38 · El visor inline. `<object>` y no `<iframe>` porque trae fallback
 * declarativo: donde el navegador no incruste PDF, se ofrece la descarga
 * del MISMO blob — no una segunda peticion.
 */
function VisorDelDocumento({ visor, refDelVisor, onCerrar }: {
  visor: DocumentoEnElVisor;
  /** La region que recibe la vista y el foco al abrir (ver el efecto de `visor`). */
  refDelVisor: RefObject<HTMLDivElement>;
  onCerrar: () => void;
}) {
  return (
    <div
      ref={refDelVisor}
      tabIndex={-1}
      role="region"
      aria-label={`Visor del documento ${visor.nombre}`}
      className="scroll-mt-4 outline-none"
    >
      <Card
        className="mt-4"
        title={visor.nombre}
        subtitle="Documento archivado en el expediente."
        actions={
          <Button size="sm" variant="secondary" icon={X} onClick={onCerrar}>
            Cerrar
          </Button>
        }
      >
        <object
          data={visor.url}
          type="application/pdf"
          aria-label={`Documento ${visor.nombre}`}
          className="w-full h-[70vh] rounded-xl border border-hairline bg-canvas"
        >
          <div className="p-6 text-body text-ink-soft">
            Este navegador no muestra PDF incrustado.{' '}
            <a href={visor.url} download={`${visor.nombre}.pdf`} className="underline text-marca-texto">
              Descarga el documento
            </a>{' '}
            para abrirlo con tu lector.
          </div>
        </object>
        {/* El Formato Verde suele tener MAS de una pagina y el scroll vive DENTRO
            del marco del PDF — sin esta linea, mas de un lector creyo que el
            documento "estaba incompleto" viendo solo la primera (28-ago-2026). */}
        <p className="text-meta text-ink-soft mt-2">
          El documento tiene varias páginas: desplázate dentro del visor para verlas
          todas, o{' '}
          <a href={visor.url} download={`${visor.nombre}.pdf`} className="underline text-marca-texto">
            descárgalo
          </a>{' '}
          para abrirlo completo.
        </p>
      </Card>
    </div>
  );
}

/** El expediente sin documentos: recién enviado es una espera; desde la bandeja, un hueco. */
function ExpedienteSinDocumentos({ recienEnviado }: { recienEnviado: boolean }) {
  return (
    <EmptyState
      icon={FileWarning}
      data-testid="expediente-vacio"
      title="El expediente aún no tiene documentos"
      // Recien enviado, la ausencia es una espera (las etapas de arriba la
      // cuentan); desde la bandeja, un hueco que puede merecer revision.
      description={recienEnviado
        ? 'El Formato Verde se está generando y aparecerá aquí solo, sin recargar.'
        : 'El Formato Verde se genera al enviar el caso. Si no aparece, avisa a soporte.'}
    />
  );
}

/**
 * 403: el caso está fuera de su ámbito (RN-60). No es una avería: se dice con el texto del
 * servidor y se ofrece la salida (volver a la pantalla de origen o, sin ella, a la bandeja).
 */
function ExpedienteFueraDeAmbito({ fallo, volverA, onVolver, onIrABandeja }: {
  fallo: Fallo;
  volverA: string | null;
  onVolver: (() => void) | undefined;
  onIrABandeja: (() => void) | undefined;
}) {
  return (
    <EmptyState icon={ShieldAlert} principal title={fallo.message} data-testid="expediente-sin-permiso"
      action={onVolver && volverA
        ? <Button variant="secondary" icon={ArrowLeft} onClick={onVolver}>Volver a {volverA}</Button>
        : onIrABandeja && <Button variant="secondary" icon={Inbox} onClick={onIrABandeja}>Ir a la bandeja</Button>} />
  );
}

/**
 * TRES FALLOS DISTINTOS, TRES REACCIONES DISTINTAS. Mandarlos todos al mismo
 * «No se pudo abrir» dejaba al usuario sin saber si reintentar, pedir permiso
 * o llamar a soporte — que son las tres cosas que puede hacer.
 */
function DocumentoQueNoAbre({ fallo }: { fallo: Fallo }) {
  return (
    <Alert
      tone={porQueNoSeAbre(fallo).tono}
      title={porQueNoSeAbre(fallo).titulo}
      data-testid={fallo.estado === 403 ? 'expediente-documento-sin-permiso' : undefined}
    >
      {/* Los cuatro desenlaces —permiso, archivo ausente, huella rota y el resto— los
          decide `porQueNoSeAbre`, compartido con la tarjeta de evidencias: es el mismo
          problema visto desde dos sitios de la misma pantalla y tiene que leerse igual. */}
      {porQueNoSeAbre(fallo).texto}
    </Alert>
  );
}

export default function Expediente({ casoId, recienEnviado = false, numeroDeCaso = null, onIrABandeja, volverA = null, onVolver, onVerCompromisos, onVerTrazabilidad }: {
  casoId: string;
  /** Recien enviado en ESTA sesion: abre con las etapas del nacimiento (T-42). */
  recienEnviado?: boolean;
  /** El numero definitivo, si vino en la respuesta del envio. */
  numeroDeCaso?: string | null;
  onIrABandeja?: () => void;
  /** Cómo se llama la pantalla de la que se vino; sin ella no se ofrece «Volver». */
  volverA?: string | null;
  onVolver?: () => void;
  /** «Ver compromisos» de la condición C-08: Disposiciones y seguimiento de este caso (`ui/MG-ILE-54.md`). */
  onVerCompromisos?: (casoId: string, numero: string) => void;
  /**
   * HU-37 (`ui/MG-ILE-69.md`): «Ver trazabilidad» abre la línea de tiempo del caso. Se ofrece si
   * `expediente.acciones_permitidas` trae `ver_trazabilidad`: el servidor ya decidió con los roles y
   * el ámbito de la trazabilidad (HU-37-C8) si esta sesión la puede abrir.
   */
  onVerTrazabilidad?: () => void;
}) {
  const [expediente, setExpediente] = useState<RespuestaDelApi | null>(null);
  // HU-48-C8 / HU-52-C7: el reintento en vuelo (por `tipo`) y su fallo (403, 409, 5xx).
  const [reintentando, setReintentando] = useState<string | null>(null);
  const [falloDeEnvio, setFalloDeEnvio] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [esperando, setEsperando] = useState(false); // 404 en curso: aun naciendo
  // RN-82 (Bug #1078): cada instante del expediente se lee en la zona del país del caso.
  const zona = useZonaDelPais()(typeof expediente?.pais === 'string' ? expediente.pais : null);

  const cargar = useCallback(async () => {
    if (!casoId) return;
    setCargando(true);
    setFallo(null);
    try {
      setExpediente(await verExpediente(casoId));
      setEsperando(false);
    } catch (error) {
      const fallo = aFallo(error);
      if (fallo.estado === 404) {
        // "Todavia no": deja el estado de espera puesto; el efecto de abajo
        // programa el siguiente intento. El mensaje terminal recien aparece
        // cuando la espera se agota.
        setEsperando(true);
      } else {
        setFallo(fallo);
      }
    } finally {
      setCargando(false);
    }
  }, [casoId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Registrar una disposición cambia C-08 y el sustento: se refrescan el expediente y el cierre.
  const [refresco, setRefresco] = useState(0);
  const refrescarTodo = useCallback(() => {
    setRefresco((n) => n + 1);
    cargar();
  }, [cargar]);

  const [intentos, setIntentos] = useState(0);
  useEffect(() => {
    if (!esperando) {
      setIntentos(0);
      return undefined;
    }
    if (intentos >= REINTENTOS_MAXIMOS) {
      setFallo(new Error(
        'El expediente tarda más de lo esperado. El caso ya quedó registrado; '
        + 'refresca en unos minutos.',
      ));
      setEsperando(false);
      return undefined;
    }
    const t = setTimeout(() => {
      setIntentos((n) => n + 1);
      cargar();
    }, REINTENTO_MS);
    return () => clearTimeout(t);
  }, [esperando, intentos, cargar]);

  // T-42 · La SEGUNDA espera: el expediente ya existe pero el Formato Verde aun
  // no. Antes el sondeo se detenia al cargar el expediente y la tabla quedaba en
  // "aun no tiene documentos" hasta que alguien refrescara a mano. Solo aplica
  // al recien enviado: llegar desde la bandeja es consulta, y ahi un expediente
  // sin documentos no es una espera, es un estado.
  //
  // `sinFormato` es un booleano derivado a proposito: cada respuesta del sondeo
  // trae un objeto `expediente` nuevo, y colgar el efecto del objeto lo haria
  // rearmarse en cada poll aunque nada hubiera cambiado.
  const [intentosDeFormato, setIntentosDeFormato] = useState(0);
  const [formatoDemorado, setFormatoDemorado] = useState(false);
  const sinFormato = recienEnviado && Boolean(expediente) && !expediente?.documentos?.length;
  useEffect(() => {
    if (!sinFormato || formatoDemorado) return undefined;
    if (intentosDeFormato >= REINTENTOS_MAXIMOS) {
      // Agotarse NO es un error: la etapa queda en "sigue en camino" y se deja
      // de sondear. El trabajo esta en el servidor; refrescar lo encontrara.
      setFormatoDemorado(true);
      return undefined;
    }
    const t = setTimeout(() => {
      setIntentosDeFormato((n) => n + 1);
      cargar();
    }, REINTENTO_MS);
    return () => clearTimeout(t);
  }, [sinFormato, formatoDemorado, intentosDeFormato, cargar]);

  // T-38 · Visor del PDF. El binario llega por la API (`contenidoDeDocumento`), no
  // por una URL del almacenamiento: el backend comprueba la huella (ADR-003) antes
  // de entregar, y el navegador solo ve un blob local.
  const [visor, setVisor] = useState<DocumentoEnElVisor | null>(null);
  const visorRef = useRef<HTMLDivElement>(null);
  const [abriendoDoc, setAbriendoDoc] = useState<string | null>(null);
  const [falloDeDoc, setFalloDeDoc] = useState<Fallo | null>(null);

  // La URL de objeto retiene el blob entero en memoria hasta que alguien la
  // revoque. Atarla al ciclo del estado `visor` cubre los tres finales de una vez:
  // cerrar, abrir otro documento encima y desmontar la pantalla.
  useEffect(() => {
    if (!visor) return undefined;
    return () => URL.revokeObjectURL(visor.url);
  }, [visor]);

  /**
   * "Ver PDF" TRAE LA VISTA Y EL FOCO al visor (7-sep-2026).
   *
   * La tarjeta del visor se monta DEBAJO de la lista de documentos. En un expediente
   * con varias filas eso la deja fuera de pantalla, y como el boton no cambia de
   * aspecto una vez termina de cargar, el resultado es que pulsarlo PARECE no hacer
   * nada: el documento estaba abierto, mas abajo, sin que nadie lo viera.
   *
   * Va en un efecto y no dentro de `verPdf` porque alli la tarjeta TODAVIA NO EXISTE
   * en el DOM — `setVisor` solo programa el render. Depende de `visor`, que es un
   * objeto NUEVO en cada apertura: asi abrir un SEGUNDO documento con el visor ya
   * abierto vuelve a traer la vista, en vez de dejarla donde estaba.
   *
   * EL FOCO VA AL CONTENEDOR, no al `<object>`: los PDF incrustados no son
   * enfocables de forma fiable entre navegadores. Enfocar la region (`tabIndex={-1}`)
   * ademas hace que el lector de pantalla la anuncie; hasta ahora quien navegaba con
   * teclado se quedaba en el boton sin enterarse de que habia aparecido nada.
   *
   * `preventScroll` porque el desplazamiento ya lo hace `scrollIntoView` con su
   * animacion: sin eso el foco da un salto seco y deshace lo suave.
   */
  useEffect(() => {
    const caja = visorRef.current;
    if (!visor || !caja) return;
    // Guarda completa y no `window.matchMedia?.(...)`: en jsdom `matchMedia` no
    // existe y el encadenado opcional a medias revienta igual (ver `LoginSimulado`).
    const consulta =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    // Suave por defecto; SECO si el sistema pide reducir movimiento. Un desplazamiento
    // largo y animado es justo lo que marea a quien activo esa preferencia.
    caja.scrollIntoView({ behavior: consulta?.matches ? 'auto' : 'smooth', block: 'start' });
    caja.focus?.({ preventScroll: true });
  }, [visor]);

  const abrirDocumento = useCallback(async (documentoId: string, nombre: string) => {
    setFalloDeDoc(null);
    setAbriendoDoc(documentoId);
    try {
      const binario = await contenidoDeDocumento(casoId, documentoId);
      setVisor({ documentoId, nombre, url: URL.createObjectURL(binario) });
    } catch (error) {
      setFalloDeDoc(aFallo(error));
    } finally {
      setAbriendoDoc(null);
    }
  }, [casoId]);
  const verPdf = (fila: RespuestaDelApi) => abrirDocumento(fila.documento_id, `${NOMBRE_DE_TIPO[fila.tipo] ?? fila.tipo} · v${fila.version}`);
  // Sprint 3: la versión trae su texto armado por el servidor («Informe FAR-03 · v2 · …»).
  const verVersion = (version: VersionDeLaFila) => abrirDocumento(version.documento_id, version.texto);

  /**
   * HU-48-C8 / HU-52-C7 · «Reintentar». 202 sin cuerpo: el expediente se refresca desde el
   * servidor (§8.1 R3) y la fila pasa a `pendiente`. Un 409 (ya generado o en curso), un 403
   * (P-03) o un 5xx se dicen con el texto del servidor, y también se refresca.
   */
  const reintentar = async (fila: FilaDelExpediente) => {
    if (fila.version_del_encargo === null) return;
    setFalloDeEnvio(null);
    setReintentando(claveDeLaFila(fila));
    try {
      // CC-02 (B9): la copia de una carta se reintenta con su autoridad; las demás, como siempre.
      if (fila.autoridad_codigo) await reintentarEncargo(casoId, fila.tipo, fila.version_del_encargo, fila.autoridad_codigo);
      else await reintentarEncargo(casoId, fila.tipo, fila.version_del_encargo);
    } catch (error) {
      setFalloDeEnvio(aFallo(error));
    } finally {
      setReintentando(null);
      cargar();
    }
  };

  // T-42 · El nacimiento visto por etapas: SOLO tras enviar en esta sesion. La
  // etapa 1 ya sale con el numero —viene en la respuesta del envio— aunque el
  // primer GET todavia de 404.
  //
  // ⚠ CP-08-13 (4-sep-2026). Esta guarda llevaba `&& !fallo`, y con eso cualquier
  // error del GET —o agotar el sondeo— hacia que el `if (fallo)` de mas abajo
  // devolviera una alerta roja EN LUGAR DE TODA LA PANTALLA, confirmacion incluida.
  // El registrador enviaba bien, su caso quedaba registrado y numerado, y lo unico
  // que veia era "No se pudo abrir el expediente": creia haberlo perdido y volvia a
  // enviar. Solo recargando aparecia. Es lo que QA reporto, y es justo lo contrario
  // de lo que HU-08 promete para este escenario.
  //
  // Ahora el fallo NO decide si se ve la confirmacion: se ve siempre, y el tropiezo
  // viaja DENTRO como aviso. El `if (fallo)` de abajo se queda para el otro camino
  // —llegar desde la bandeja a un expediente que no abre—, donde no hay ninguna
  // confirmacion que proteger y la alerta a pantalla completa si es correcta.
  if (recienEnviado && !expediente) {
    return (
      <EtapasDelNacimiento
        numero={numeroDeCaso}
        expedienteAbierto={false}
        formatoArchivado={false}
        formatoEnCamino={false}
        onIrABandeja={onIrABandeja}
        tropiezo={fallo}
      />
    );
  }

  if (esperando) {
    return (
      <Alert tone="info" title="Generando el expediente…">
        El caso quedó enviado y numerado. El expediente aparecerá aquí en unos segundos.
      </Alert>
    );
  }

  // `&& !expediente`: el sondeo del Formato Verde (T-42) vuelve a llamar a
  // `cargar` con el expediente ya en pantalla, y sin esta guarda cada poll
  // parpadearia a "Abriendo el expediente…" durante un instante.
  // Cargando y con error la pantalla tambien tiene su `h1` y el titulo de la pestana (WCAG 1.3.1
  // y 2.4.2, MG-ILE-710): antes solo habia un parrafo, y el lector de pantalla no sabia donde estaba.
  const encabezadoSinExpediente = <PageHeader eyebrow="Expediente" title="Expediente del caso" />;
  if (cargando && !expediente) {
    return (
      <>
        {encabezadoSinExpediente}
        <p className="p-6 text-body text-ink-soft" data-testid="expediente-cargando">Abriendo el expediente…</p>
      </>
    );
  }

  // 403: el caso está fuera de su ámbito (RN-60). No es una avería: se dice con el texto del
  // servidor y se ofrece la salida.
  if (fallo?.estado === 403) {
    return <ExpedienteFueraDeAmbito fallo={fallo} volverA={volverA} onVolver={onVolver} onIrABandeja={onIrABandeja} />;
  }

  if (fallo) {
    return (
      <>
        {encabezadoSinExpediente}
        <Alert tone="danger" title="No se pudo abrir el expediente" detalleTecnico={fallo.detalleTecnico} data-testid="expediente-error">
          {fallo.estado === 404
            ? 'Este caso todavía no tiene expediente. Se crea al enviar el caso.'
            : fallo.message}
          <div className="mt-2">
            <Button size="sm" variant="secondary" icon={RotateCw} onClick={() => { cargar(); }}>Reintentar</Button>
          </div>
        </Alert>
      </>
    );
  }

  if (!expediente) return null;

  const columnas = [
    { key: 'tipo', label: 'Documento' },
    { key: 'version', label: 'Versión' },
    { key: 'generado_en', label: 'Generado' },
    { key: 'pii', label: <span className="sr-only">Datos personales</span> },
    { key: 'acciones', label: '' },
  ];

  const celda = (fila: RespuestaDelApi, { key: columna }: ColumnaDeTabla) => {
    if (columna === 'tipo') {
      return (
        <div>
          <p className="font-semibold text-ink text-body">
            {/* HU-22-C1: «Cargo de la autoridad · 2026-09-11»; el servidor etiqueta cada tipo. */}
            {fila.etiqueta ?? NOMBRE_DE_TIPO[fila.tipo] ?? fila.tipo}
          </p>
          {/* La huella (ADR-003) se sigue comprobando al abrir el documento, pero no se
              pinta: al usuario no le decia nada (decisión del operador, 5-oct-2026). */}
        </div>
      );
    }
    if (columna === 'version') {
      return (
        <span className="flex items-center gap-2">
          <Mono>v{fila.version}</Mono>
          {fila.es_la_vigente && <StatusBadge tone="success">Vigente</StatusBadge>}
        </span>
      );
    }
    if (columna === 'generado_en') return comoFechaYHora(fila.generado_en, zona);
    if (columna === 'acciones') {
      // EL BOTON SE OFRECE TAMBIEN EN LOS DOCUMENTOS L2 (22-sep-2026).
      //
      // Antes se ocultaba —«mejor no ofrecerlo que ofrecerlo y fallar»— porque el
      // endpoint rechazaba a todo el mundo. Ya no: sirve a quien tiene concedido el
      // permiso L2 y deja asiento de la lectura (RN-09, HU-33). Ocultarlo ahora seria
      // esconder un documento que esta persona SI puede abrir.
      //
      // A quien no tenga permiso el servidor le responde 403 y la pantalla lo explica
      // en el aviso de arriba. Es la eleccion correcta entre las dos posibles: el front
      // no sabe si la cuenta tiene el permiso —es nominal y vive en el servidor— y
      // fingir que lo sabe le haria ocultar el boton a quien si puede.
      return (
        <Button size="sm" variant="secondary" icon={Eye}
          disabled={abriendoDoc === fila.documento_id}
          onClick={() => verPdf(fila)}>
          {abriendoDoc === fila.documento_id ? 'Abriendo…' : 'Ver PDF'}
        </Button>
      );
    }
    if (columna === 'pii' && fila.contiene_pii) {
      // LA MARCA SE QUEDA, EL BLOQUEO NO (22-sep-2026).
      //
      // Antes esta caja explicaba por que NO habia boton. Ahora el boton existe y la
      // caja sigue haciendo falta, pero dice otra cosa: que lo que se va a abrir es
      // dato de salud de una persona y que quedara registrado quien lo abrio. Es un
      // aviso previo, no un rechazo — el que convierte una garantia legal en algo que
      // el usuario entiende ANTES de pulsar, en vez de en un 403 despues.
      return (
        <div>
          <StatusBadge tone="danger" icon={ShieldAlert}>Dato de personas</StatusBadge>
          <p className="text-meta text-ink-soft mt-1 max-w-[12.5rem]">
            Contiene datos de una persona (L2). Abrirlo exige permiso y queda
            registrado en la auditoría.
          </p>
        </div>
      );
    }
    return null;
  };

  // HU-26: las filas del Sprint 3 y las que ya tienen algo que mostrar (versión o encargo).
  const filas = (expediente.filas ?? []) as FilaDelExpediente[];
  const filasConDocumento = filas.filter((f) => f.versiones.length || f.version_del_encargo !== null);
  const datoDePersonas: Record<string, boolean> = Object.fromEntries(
    (expediente.documentos ?? []).map((d: RespuestaDelApi) => [d.documento_id, Boolean(d.contiene_pii)]),
  );
  const notificacion = expediente.notificacion as Notificacion | null | undefined;
  // ui/MG-ILE-54, -53, -56 y -69: lo que el servidor ya resolvió para esta sesión. Sin `caso`
  // (servidor anterior) no se ofrece ninguna acción sobre el caso (§8.1 R4).
  const caso = (expediente.caso ?? null) as CasoDelExpediente | null;
  const disposiciones = (expediente.disposiciones ?? []) as DisposicionDelExpediente[];
  const accionesDelExpediente = (expediente.acciones_permitidas ?? []) as string[];
  const puedeRegistrarDisposicion = Boolean(caso?.acciones_permitidas.includes(ACCION_REGISTRAR_DISPOSICION));

  // Sprint 3 (AT S3 §2.2.4): las filas por gravedad; un servidor anterior solo trae
  // `documentos[]` y se pinta como antes; sin ninguno de los dos, el expediente vacío.
  const documentosDelCaso = () => {
    if (filasConDocumento.length) {
      return (
        <DocumentosDelExpediente filas={filas} datoDePersonas={datoDePersonas} abriendo={abriendoDoc}
          reintentando={reintentando} onVerPdf={(version) => { verVersion(version); }} onReintentar={(f) => { reintentar(f); }} />
      );
    }
    if (!filas.length && expediente.documentos?.length) {
      return <DataTable columns={columnas} rows={expediente.documentos} renderCell={celda} sinMarco data-testid="expediente-documentos" />;
    }
    return <ExpedienteSinDocumentos recienEnviado={recienEnviado} />;
  };

  return (
    <>
      {recienEnviado && (
        <EtapasDelNacimiento
          numero={numeroDeCaso ?? expediente.numero_de_caso}
          expedienteAbierto
          formatoArchivado={Boolean(expediente.documentos?.length)}
          formatoEnCamino={formatoDemorado}
          onIrABandeja={onIrABandeja}
        />
      )}
      <PageHeader
        eyebrow="Casos · Expediente"
        hu="HU-09 · F-02"
        title={<>Expediente <Codigo>{expediente.numero_de_caso}</Codigo></>}
        subtitle="Documentos, evidencias y avisos del caso."
        icon={FolderOpen}
        actions={
          <AccionesDeLaCabecera expediente={expediente} volverA={volverA} onVolver={onVolver}
            puedeVerTrazabilidad={accionesDelExpediente.includes(ACCION_VER_TRAZABILIDAD)} onVerTrazabilidad={onVerTrazabilidad} />
        }
      />

      {/* HU-27-C7 · HU-28-C1 y C7: estado, «cerrado por», reaperturas y gravedad, de `caso.*`. */}
      {caso && <CabeceraDelCaso caso={caso} zonaHoraria={zona} />}

      {!expediente.esta_completo && <FaltantesDelExpediente expediente={expediente} />}

      {falloDeEnvio && (
        <div className="mt-4">
          <Alert tone={falloDeEnvio.estado === 409 || falloDeEnvio.estado === 403 ? 'warning' : 'danger'}
            title="No se pudo reintentar la generación" detalleTecnico={falloDeEnvio.detalleTecnico}
            data-testid="expediente-error-envio" onCerrar={() => setFalloDeEnvio(null)}>
            {falloDeEnvio.message}
          </Alert>
        </div>
      )}

      <Card className="mt-4" pad={false}>
        {/* Sprint 3 (AT S3 §2.2.4): las filas por gravedad. Un servidor anterior solo trae
            `documentos[]`, y se pinta como antes. */}
        {documentosDelCaso()}
      </Card>

      {/* HU-49-C9 / HU-07: las evidencias del caso, cada una con su ORIGEN —«Evidencia del
          registro» o «Ampliación del informe (act. 8)»— y quien la subio. Son archivos
          adjuntos, no documentos producidos: por eso van en su propia tarjeta. */}
      <EvidenciasDelCaso casoId={casoId} evidencias={(expediente.evidencias ?? []) as Evidencia[]} zona={zona} />

      {/* HU-21-C6/C9 / HU-22: la notificacion a la autoridad: las DOS fechas de RN-17 como
          dos datos distintos, y quien firmo y quien presento como dos personas. */}
      {notificacion && <NotificacionDelCaso n={notificacion} zona={zona} />}

      {/* HU-25: solo los casos con trámite ante una autoridad (graves) tienen disposiciones; las
          que ya hay se ven siempre. */}
      {(notificacion?.autoridad || disposiciones.length > 0) && (
        <DisposicionesDelCaso casoId={casoId} numero={expediente.numero_de_caso} autoridad={notificacion?.autoridad ?? null}
          disposiciones={disposiciones} puedeRegistrar={puedeRegistrarDisposicion} onCambio={refrescarTodo} />
      )}

      {/* HU-47 · CP-47-06 y CP-47-10. Va despues de los documentos: primero QUE se
          genero, luego A QUIEN se le aviso. Se pinta solo, aislado del resto. Bug #1098
          (ajuste de cierre): solo si el servidor ofrece `ver_avisos`; al Especialista
          invitado no se le ofrece (RN-60) y la tarjeta ni siquiera pregunta. */}
      {accionesDelExpediente.includes(ACCION_VER_AVISOS) && (
        <AvisosDelCaso casoId={casoId} cartas={notificacion?.cartas ?? []} />
      )}

      {/* HU-27 · EL CIERRE DEL CASO, TAMBIEN PARA LOS NO GRAVES (bug 702, CP-E2E-03).
          Vivia solo dentro del tramite regulatorio, y un caso no grave no tiene tramite
          (RN-15): no habia forma de cerrarlo por pantalla, aunque el backend supiera
          hacerlo —C-07 no bloquea sin tramite—. Va al final porque es el ultimo acto del
          caso, y se pinta sola: la tarjeta y «Cerrar caso» siguen el rol de la sesion (no
          hay `cierre.acciones_permitidas`, vacio anotado), «Reabrir» sale de
          `caso.acciones_permitidas`, y desaparece ante un 404. */}
      <CierreDelCaso key={`cierre-${refresco}`} casoId={casoId} numeroDeCaso={expediente.numero_de_caso} caso={caso} onCambio={cargar}
        onVerCompromisos={onVerCompromisos ? () => onVerCompromisos(casoId, expediente.numero_de_caso) : undefined} />

      {/* HU-28-C3 y C4: los cierres y reaperturas, para todos los que abren el expediente. */}
      {caso && <HistorialDeCierres cierres={caso.cierres} zonaHoraria={zona} />}

      {falloDeDoc && <DocumentoQueNoAbre fallo={falloDeDoc} />}

      {visor && <VisorDelDocumento visor={visor} refDelVisor={visorRef} onCerrar={() => setVisor(null)} />}

      {/* HU-41 (`ui/MG-ILE-66.md`): no existe «Eliminar» para ningún rol; el servidor responde
          405 (RN-47). La nota es la de la spec. */}
      <p className="mt-3 text-meta text-ink-soft" data-testid="expediente-conservacion">
        Este expediente se conserva de forma permanente. No puede eliminarse.
      </p>
      <p className="mt-1 text-meta text-ink-soft">
        Los documentos no se editan ni se borran: cada corrección es una versión nueva.
      </p>
    </>
  );
}
